import {Collapse, createStyles} from '@mantine/core';
import {useReducedMotion} from '@mantine/hooks';
import React, {useEffect, useState} from 'react';

const REVEAL_DURATION_MS = 200;

const useStyles = createStyles((_theme, {shown}: {shown: boolean}) => ({
  fade: {
    opacity: shown ? 1 : 0,
    transition: `opacity ${REVEAL_DURATION_MS}ms ease`,
  },
}));

interface AnimatedRevealProps {
  children: React.ReactNode;
}

/**
 * Eases the omnibox direct-answer block in with a height expansion and fade instead of letting it
 * snap into place and shove the results below it down. The direct answer streams in after the fast
 * search results have already rendered, so this softens its arrival.
 *
 * Animates once, on first mount. Respects prefers-reduced-motion by rendering immediately.
 */
export default function AnimatedReveal({children}: AnimatedRevealProps): JSX.Element {
  const reducedMotion = useReducedMotion();
  const [shown, setShown] = useState(false);
  const {classes} = useStyles({shown});

  useEffect(() => {
    // Flip open on the next frame so Collapse animates from closed -> open rather than mounting
    // already open (which would skip the transition).
    const id = requestAnimationFrame(() => {
      setShown(true);
    });
    return () => {
      cancelAnimationFrame(id);
    };
  }, []);

  if (reducedMotion) {
    return <>{children}</>;
  }

  return (
    <Collapse in={shown} transitionDuration={REVEAL_DURATION_MS}>
      <div className={classes.fade}>{children}</div>
    </Collapse>
  );
}
